class CollectionGrid extends HTMLElement {
  #controller = null;

  render(params = {}) {
    console.log("CG: render called with", params);
    this.classList.add("loading");
    this.#controller?.abort();
    this.#controller = new AbortController();

    const url = new URL(location.pathname, location.origin);

    url.searchParams.set("section_id", this.id);

    for (const [param, value] of params.entries()) {
      url.searchParams.append(param, value);
    }
    console.log("CG: fetch url", url.href);

    fetch(url.href, { signal: this.#controller.signal })
      .then((response) => {
        console.log("CG: response received");
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fragment = doc.getElementById(this.id);

        this.innerHTML = fragment.innerHTML;
      })
      .catch((error) => {
        if (error.name == "AbortError") {
          console.log("CG: aborted");
          return;
        }
        console.log("CG: fetch error", error);
      })
      .finally(() => {
        this.classList.remove("loading");
        this.#controller = null;
        this.dispatchEvent(new CustomEvent("collectiongrid:rendered", { bubbles: true })
        );
      });
  }
}

customElements.define("collection-grid", CollectionGrid);

class CollectionFilters extends Theme.Component {
  connectedCallback() {
    this.events();
    this.form = this.querySelector("form");
    this.sortWrapper = this.querySelector(".collection-sort");
    this.sortModal = this.querySelector(".collection-sort-modal");

    this.applyFilteredStateFromURL();
    this.applyCheckedFromURL();

    if (!this.form) return;

    this.onInput = this.onInput.bind(this);
    this.form.addEventListener("change", this.onInput);

    document.addEventListener("click", (e) => {
      if (!this.sortWrapper || !this.sortModal) return;

      const clickedInside = this.sortWrapper.contains(e.target);
      const clickedTrigger =
        this.openSortTrigger && this.openSortTrigger.contains(e.target);

      if (!clickedInside && !clickedTrigger) {
        this.sortWrapper.classList.remove("is-open");
      }
    });

    document.addEventListener("collectiongrid:rendered", () => {
      this.classList.remove("loading");
    });
  }

  applyCheckedFromURL() {
    const params = new URLSearchParams(window.location.search);
    const buttons = this.querySelectorAll('.sales-button');
    buttons.forEach(button => {
      const input = button.querySelector('input');
      if (!input) return;
      if (params.toString().includes(input.value)) {
        button.classList.add('is-checked');
      }
    });
  }

  toggleSort() {
    if (!this.sortWrapper) return;
    if (!this.sortWrapper.classList.contains("is-open")) {
      this.sortWrapper.classList.add("is-open");
    } else {
      this.sortWrapper.classList.remove("is-open");
    }
  }

  clearAll() {
    this.form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = false;
      const label = cb.closest("label");
      if (label) label.classList.remove("is-checked");
    });

    const sortChecked = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortChecked) sortChecked.checked = false;

    this.classList.remove("is-filtered");
    this.submitCombinedParams();
  }

  onInput(event) {
    if (event.target.type === "checkbox") {
      const label = event.target.closest("label");
      if (label) {
        if (event.target.checked) label.classList.add("is-checked");
        else label.classList.remove("is-checked");
      }
      this.submitCombinedParams();
      return;
    }

    if (event.target.name === "sort_by") {
      this.submitCombinedParams();
      if (this.sortWrapper) this.sortWrapper.classList.remove("is-open");
      return;
    }
  }

  submitCombinedParams() {
    const params = new URLSearchParams();

    this.form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      if (cb.checked) params.append(cb.name, cb.value);
    });

    const sortRadio = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortRadio) params.set("sort_by", sortRadio.value);

    this.classList.add("loading");
    history.pushState(
      { searchParams: params.toString() },
      "",
      `${location.pathname}?${params}`
    );
    this.updateCollection(params);
    this.classList.add("is-filtered");
  }

  onPopState(event) {
    const searchParams = event.state?.searchParams;
    if (!searchParams) return;

    const params = new URLSearchParams(searchParams);
    this.updateCollection(params);
    this.classList.toggle("is-filtered", Array.from(params.keys()).length > 0);
  }

  updateCollection(params) {
    const collection = document.querySelector("collection-grid");
    if (collection) collection.render(params);
  }

  applyFilteredStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const hasRealFilters = Array.from(params.keys()).some(
      (key) => !key.includes("sort_by")
    );
    if (hasRealFilters) {
      this.classList.add("is-filtered");
    } else {
      this.classList.remove("is-filtered");
    }
  }
}

customElements.define("collection-filters", CollectionFilters);

class CollectionFiltersDrawer extends Theme.Component {
  connectedCallback() {
    this.events();
    this.scrollAnchor = null;
    this.form = this.querySelector("form");
    if (!this.form) return;
    this.onInput = this.onInput.bind(this);
    this.form.addEventListener("change", this.onInput);
  }

  onInput(event) {
    const target = event.target;

    if (target.type === "checkbox" || target.name === "sort_by") {
      this.scrollAnchor = { name: target.name, value: target.value };
      this.submitCombinedParams();
    }
  }

  submitCombinedParams() {
    const params = new URLSearchParams();

    this.form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      if (cb.checked) params.append(cb.name, cb.value);
    });

    const sortRadio = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortRadio) params.set("sort_by", sortRadio.value);

    history.pushState(
      { searchParams: params.toString() },
      "",
      `${location.pathname}?${params}`
    );

    this.updateCollection(params);
    this.render(params);
  }

  render(params = null) {
    this.classList.add("state--loading");
    const currentContent = this.querySelector("#collection-filters-drawer");
    if (!currentContent) {
      this.classList.remove("state--loading");
      return;
    }

    const url = new URL(location.pathname, location.origin);
    url.searchParams.set("sections", "collection-filters-drawer");

    const nextParams = params instanceof URLSearchParams ? params : new URLSearchParams(window.location.search);

    for (const [key, value] of nextParams.entries()) {
      if (key === "sections") continue;
      url.searchParams.append(key, value);
    }

    function getNewContent(response) {
      const string = JSON.parse(response)["collection-filters-drawer"];
      const parser = new DOMParser();
      const doc = parser.parseFromString(string, "text/html");
      return doc.querySelector("#collection-filters-drawer");
    }

    return fetch(url.href)
      .then((response) => response.text())
      .then((response) => {
        const nextContent = getNewContent(response);
        if (!nextContent) return;

        currentContent.replaceWith(nextContent);

        this.form = this.querySelector("form");
        if (this.form) {
          this.form.removeEventListener("change", this.onInput);
          this.form.addEventListener("change", this.onInput);
        }
        if (this.scrollAnchor) {
          const name = CSS.escape(this.scrollAnchor.name);
          const value = CSS.escape(this.scrollAnchor.value);
          const nextInput = this.querySelector(`input[name="${name}"][value="${value}"]`);
          const scroller = this.querySelector(".collection-filters-content");

          if (nextInput && scroller) {
            setTimeout(() => {
              const top = nextInput.offsetTop - (scroller.clientHeight / 2) + (nextInput.clientHeight / 2);
              scroller.scrollTop = top;
            }, 0);
          } else if (nextInput) {
            setTimeout(() => {
              nextInput.scrollIntoView({ block: "center" });
            }, 0);
          }

          this.scrollAnchor = null;
        }
      })
      .then(() => this.events())
      .finally(() => {
        this.classList.remove("state--loading");
      });
  }

  updateCollection(params) {
    const grid = document.querySelector("collection-grid");
    if (grid) grid.render(params);
  }

  clearAll() {
    if (!this.form) return;

    this.form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = false;
    });

    const sortChecked = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortChecked) sortChecked.checked = false;

    const params = new URLSearchParams();

    history.pushState(
      { searchParams: params.toString() },
      "",
      `${location.pathname}`
    );

    this.updateCollection(params);
    this.render(params);
  }

  show() {
    this.classList.add("state--active");
  }

  hide() {
    this.classList.remove("state--active");
  }
}
customElements.define("collection-filters-drawer", CollectionFiltersDrawer);
